"use client";

import {
  CardHeader,
  CardHeaderDivider,
  CardTitle,
  CardContent,
} from "../../ui/card";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import { useAudioLevel } from "@/app/(v1)/hooks/use-audio-level";
import { cn } from "@/lib/utils";
import {
  Wifi,
  WifiOneBar,
  WifiTwoBar,
  MicFilled,
  VideocamFilled,
  LockFilled,
  ClockFilled,
  VolumeUp,
} from "@/components/ui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserMediaStream, userMediaStore } from "@/app/(v1)/hooks/user-media/media-monitor";
import { agentStore } from "@/app/(v1)/hooks/voice-agent-store";
import { UserMedia, useUserMediaContext } from "@/app/(v1)/ui/user-media";
import { motion } from "framer-motion";
import { useRouter, useSearchParams } from "next/navigation";
import { InterviewParams, interviewParamsToSearchParams } from "@/app/(v1)/utils";
import { variants, layoutIds } from "@/app/(v1)/animations/card-animations";
import FancyTodoItem from "@/components/fancy-checkbox";
import { useInterviewStore } from "@/app/(v1)/hooks/use-interview-store";
import { useCustomAnalytics } from "@/app/api/analytics/client";
import { useI18n } from "@/locales/client";
import { createNeedleRenderer } from "@/locales/needle-renderer";
import { useLocale } from "@/app/(v1)/hooks/locale";
import { transitionState } from "@/app/(v1)/actions/ensureState";

export function OnboardingScreen(props: { data: InterviewParams }) {
  const stream = useUserMediaStream();
  const {
    audioDevices,
    videoDevices,
    status: audioStatus,
    error: audioError,
    videoHealth,
    videoError,
    isAudioActive,
    isVideoActive,
    audioDeviceId,
    videoDeviceId,
    initializationState,
  } = useUserMediaContext();
  const { context } = useInterviewStore();
  const t = useI18n();
  const { locale } = useLocale();
  const renderNeedles = useMemo(
    () =>
      createNeedleRenderer({
        bold: (value) => `<b>${value}</b>`,
      }),
    [],
  );
  const brandAssets = context?.brandedAssets ?? {
    logoUrl: null,
    colors: {
      background: "hsl(0, 0%, 0%)",
      foreground: "hsl(0, 0%, 100%)",
    },
  };
  const router = useRouter();
  const analytics = useCustomAnalytics({
    companyId: context?.company?.id as string,
    userId: context?.candidate?.org_candidate_id as string,
  });

  // String-based permission flow state
  // idle -> requesting -> granted/denied/error -> configuring -> ready
  const getAudioFlowState = (): string => {
    // Check loading state first - don't show errors while requesting
    if (initializationState?.audio === "loading") return "loading";
    if (audioStatus === "idle" && !stream) return "idle";
    if (audioStatus === "error") return "error";
    if (audioDevices.length === 0) return "no-devices";
    if (!audioDeviceId) return "selecting";
    if (!isAudioActive) return "inactive";
    return "ready";
  };

  const getVideoFlowState = (): string => {
    // Check loading state first - don't show errors while requesting
    if (initializationState?.video === "loading") return "loading";
    if (videoHealth === "idle" && !stream) return "idle";
    if (videoHealth === "error") return "error";
    if (videoDevices.length === 0) return "no-devices";
    if (!videoDeviceId) return "selecting";
    if (!isVideoActive) return "inactive";
    return "ready";
  };

  const audioFlowState = getAudioFlowState();
  const videoFlowState = getVideoFlowState();

  // Error messages - only show when user is in "selecting" state (permissions granted but no device selected)
  // or when there's an actual error/no-devices state
  const audioBlockMessage =
    audioFlowState === "loading"
      ? null
      : audioFlowState === "no-devices"
        ? t("onboarding.validation.noMicrophoneDetected")
        : audioFlowState === "selecting"
          ? t("onboarding.validation.microphoneNotSelected")
          : audioFlowState === "error"
            ? (audioError ?? t("onboarding.validation.microphoneSignalIssue"))
            : null;

  const videoBlockMessage =
    videoFlowState === "loading"
      ? null
      : videoFlowState === "no-devices"
        ? t("onboarding.validation.noCameraDetected")
        : videoFlowState === "selecting"
          ? t("onboarding.validation.cameraNotSelected")
          : videoFlowState === "error"
            ? (videoError ?? t("onboarding.validation.cameraSignalIssue"))
            : null;

  const canStartInterview =
    audioFlowState === "ready" && videoFlowState === "ready";

  useEffect(() => {
    analytics.trackPageView("onboarding_screen");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <motion.div
        layoutId={layoutIds.card.header}
        variants={variants.card.header}
        initial="initial"
        animate="animate"
      >
        <CardHeader className="~mb-6/12">
          {brandAssets.logoUrl && (
            <>
              <Image
                alt={`${props.data.company.name} logo`}
                src={brandAssets.logoUrl}
                width={120}
                height={120}
              />
              <CardHeaderDivider orientation="vertical" className="h-14" />
            </>
          )}
          <CardTitle>
            {t("onboarding.title")} <br />
            <span
              dangerouslySetInnerHTML={{
                __html: renderNeedles(
                  t("onboarding.titleContd", {
                    jobOpeningRole: context?.candidate?.position ?? "",
                    companyName: props.data.company.name,
                  }),
                ),
              }}
            />
          </CardTitle>
        </CardHeader>
      </motion.div>
      <motion.div
        layoutId={layoutIds.card.content}
        variants={variants.card.content}
        initial="initial"
        animate="animate"
      >
        <CardContent className="flex flex-col md:flex-row ~gap-12/3 w-full">
          <div className="flex flex-col w-full max-w-lg @container">
            <UserMediaPlayer
              onMicrophoneIntent={analytics.trackMicrophonePermissionIntent}
              onMicrophoneSuccess={analytics.trackMicrophonePermissionSuccess}
              onCameraIntent={analytics.trackCameraPermissionIntent}
              onCameraSuccess={analytics.trackCameraPermissionSuccess}
            />
            {audioBlockMessage && (
              <motion.div
                variants={variants.list.item}
                initial="initial"
                animate="animate"
                className="mt-4"
              >
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 shadow-sm">
                  <p className="font-semibold">
                    {t("onboarding.validation.microphoneRequired")}
                  </p>
                  <p className="text-sm text-red-700/90">{audioBlockMessage}</p>
                </div>
              </motion.div>
            )}
            {videoBlockMessage && (
              <motion.div
                variants={variants.list.item}
                initial="initial"
                animate="animate"
                className="mt-4"
              >
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 shadow-sm">
                  <p className="font-semibold">
                    {t("onboarding.validation.cameraRequired")}
                  </p>
                  <p className="text-sm text-red-700/90">{videoBlockMessage}</p>
                </div>
              </motion.div>
            )}
          </div>
          <div className="w-full flex flex-col gap-10">
            <BeforeStarting />
            <motion.div
              layoutId={layoutIds.cta}
              className="flex flex-row justify-end"
              variants={variants.cta}
              initial="initial"
              animate="animate"
            >
              <Button
                variant={"branded"}
                onClick={async () => {
                  const searchParams = new URLSearchParams(
                    typeof window !== "undefined" ? window.location.search : "",
                  );
                  await Promise.all([
                    transitionState("onboarded"),
                    analytics.candidateIntentionStartInterview(),
                  ]);

                  router.push(
                    `${locale}/start-interview?${searchParams.toString()}`,
                  );
                }}
                size={"xl"}
                disabled={!canStartInterview}
              >
                {t("onboarding.cta")}
              </Button>
            </motion.div>
          </div>
        </CardContent>
      </motion.div>
      {process.env.NODE_ENV === "development" && (
        <UserMedia.DevDeviceControlPanel />
      )}
    </>
  );
}

interface UserMediaPlayerAnalyticsHandlers {
  onMicrophoneIntent?: () => void;
  onMicrophoneSuccess?: () => void;
  onCameraIntent?: () => void;
  onCameraSuccess?: () => void;
}

function UserMediaPlayer({
  onMicrophoneIntent,
  onMicrophoneSuccess,
  onCameraIntent,
  onCameraSuccess,
}: UserMediaPlayerAnalyticsHandlers) {
  const t = useI18n();
  const { initialize, isAudioActive, isVideoActive } = useUserMediaContext();
  const [todoState, setTodoState] = useState({
    audio: isAudioActive,
    video: isVideoActive,
  });
  const audioWasActive = useRef(isAudioActive);
  const videoWasActive = useRef(isVideoActive);

  useEffect(() => {
    if (!audioWasActive.current && isAudioActive) {
      onMicrophoneSuccess?.();
    }
    audioWasActive.current = isAudioActive;
  }, [isAudioActive, onMicrophoneSuccess]);

  useEffect(() => {
    if (!videoWasActive.current && isVideoActive) {
      onCameraSuccess?.();
    }
    videoWasActive.current = isVideoActive;
  }, [isVideoActive, onCameraSuccess]);

  const handleAudioDeviceChange = useCallback((_deviceId: string) => {
    const agent = agentStore.getState().instance;
    if (!agent) return;

    const monitor = userMediaStore.getState().monitor;
    const customAudioTrack = monitor?.customAudioTrack ?? null;

    if (!customAudioTrack) return;

    agent.setCustomInputSettings({
      audio: {
        settings: {
          customTrack: customAudioTrack,
        },
      },
    });
  }, []);

  const todoContent = (
    <div className="max-w-xs gap-2 flex flex-col items-start justify-center text-start text-sm bg-white rounded-sm border border-border/90 p-3 shadow-sm relative overflow-hidden">
      <p className="text-sm text-gray-700 leading-relaxed mb-4">
        {t("onboarding.allowPanel.title")}
      </p>
      <FancyTodoItem
        label={t("onboarding.allowPanel.allowSentence", {
          device: t("onboarding.allowPanel.microphone"),
        })}
        buttonLabel={t("onboarding.allowPanel.allowBtn")}
        onToggle={() => {
          onMicrophoneIntent?.();
          if (todoState.video) {
            initialize("audio-video");
          } else {
            initialize("audio");
          }
          setTodoState((s) => ({ ...s, audio: true }));
        }}
      />
      <FancyTodoItem
        label={t("onboarding.allowPanel.allowSentence", {
          device: t("onboarding.allowPanel.camera"),
        })}
        buttonLabel={t("onboarding.allowPanel.allowBtn")}
        onToggle={() => {
          onCameraIntent?.();
          if (todoState.audio) {
            initialize("audio-video");
          } else {
            initialize("video");
          }
          setTodoState((s) => ({ ...s, video: true }));
        }}
      />
    </div>
  );
  const introductionDone = todoState.video && todoState.audio;
  return (
    <>
      <motion.div
        layoutId={layoutIds.player.root}
        variants={variants.player.root}
        initial="initial"
        animate="animate"
      >
        <UserMedia.Player className="w-full">
          {!introductionDone ? (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-muted/80 p-6 text-center">
              {todoContent}
            </div>
          ) : null}
          <UserMedia.Overlay>
            <WifiMeasure />
            <UserMedia.OverlayFooter className="gap-3 bottom-0 left-0 py-0 px-3">
              <MediaPlayerIdentity />
            </UserMedia.OverlayFooter>
          </UserMedia.Overlay>
        </UserMedia.Player>
      </motion.div>
      {introductionDone && (
        <motion.div
          layoutId={layoutIds.player.footer}
          variants={variants.player.footer}
          initial="initial"
          animate="animate"
        >
          <UserMedia.Controls>
            <div className="flex flex-row">
              <UserMedia.AudioControl onDeviceChange={handleAudioDeviceChange} />
              <UserMedia.VideoControl />
            </div>
            <UserMedia.SpeakerControl />
          </UserMedia.Controls>
        </motion.div>
      )}
    </>
  );
}

export function VoiceIndicator(props: { stream: MediaStream }) {
  const { levels, stream } = useAudioLevel({
    stream: props.stream,
    smoothing: 0.45,
    channels: 32,
  });

  return (
    <div className="flex items-center justify-center gap-[3px] rounded-sm size-5 overflow-x-clip">
      {levels.map((level, i) => (
        <div
          key={i}
          className="bg-current rounded-full transition-all duration-75"
          style={{
            minWidth: 3,
            height: `${Math.max(4, level * 32)}px`,
            opacity: stream
              ? Math.max(
                  0.3,
                  1 -
                    (Math.abs(i - levels.length / 2) / (levels.length / 2)) *
                      0.8,
                )
              : 0.3,
          }}
        />
      ))}
    </div>
  );
}

export const MediaPlayerIdentity = () => {
  const userStream = useUserMediaStream();
  const { context } = useInterviewStore();
  if (!userStream) return null;
  return (
    <motion.div
      className="flex items-center gap-0 -ml-3"
      variants={variants.player.footer}
      initial="initial"
      animate="animate"
    >
      <div className="z-20 scale-[0.47] rounded-full bg-[hsl(218_100%_84%)] p-3 text-[hsl(218_100%_30%)]">
        <VoiceIndicator stream={userStream} />
      </div>
      <span
        className="text-sm font-medium text-white -ml-1 shadow-sm"
        style={{ textShadow: "0 0px 2px rgba(0, 0, 0, 0.5)" }}
      >
        {context?.candidate?.name}
      </span>
    </motion.div>
  );
};

export const WifiMeasure = (props: { showText?: boolean } = {}) => {
  const { showText = true } = props;
  const [wifiSpeed, setWifiSpeed] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialPing, setHasInitialPing] = useState(false);

  const measureWifiSpeed = useCallback(async () => {
    try {
      if (!hasInitialPing) {
        setIsLoading(true);
      }
      const startTime = performance.now();

      // Single request for measurement
      const testRequest = fetch("https://api.hirevoice.ai/health", {
        method: "GET",
        cache: "no-cache",
      });

      const response = await testRequest;
      const endTime = performance.now();
      const duration = endTime - startTime - 50;

      // Speed calculation based on latency and success
      let speed: number;
      if (!response.ok) {
        speed = 1; // No connection
      } else if (duration < 50) {
        speed = 3; // Excellent connection
      } else if (duration < 200) {
        speed = 3; // Good connection
      } else if (duration < 400) {
        speed = 2; // Fair connection
      } else if (duration < 600) {
        speed = 2; // Slow but working
      } else {
        speed = 1; // Poor connection
      }

      setWifiSpeed(speed);
    } catch (error) {
      console.error("Failed to measure wifi speed:", error);
      setWifiSpeed(1);
    } finally {
      if (!hasInitialPing) {
        setIsLoading(false);
        setHasInitialPing(true);
      }
    }
  }, [hasInitialPing]);

  useEffect(() => {
    measureWifiSpeed();
    const interval = setInterval(measureWifiSpeed, 3000); // Less frequent testing
    return () => clearInterval(interval);
  }, [measureWifiSpeed]);

  const renderDots = () => {
    if (isLoading) {
      return (
        <div className="flex gap-0.5">
          <div className="size-[3px] bg-gray-400 rounded-full animate-pulse"></div>
          <div
            className="size-[3px] bg-gray-400 rounded-full animate-pulse"
            style={{ animationDelay: "0.2s" }}
          ></div>
          <div
            className="size-[3px] bg-gray-400 rounded-full animate-pulse"
            style={{ animationDelay: "0.4s" }}
          ></div>
        </div>
      );
    }

    return (
      <div className="flex gap-0.5">
        {[1, 2, 3].map((dot) => {
          let dotColor = "bg-gray-300";
          if (wifiSpeed && wifiSpeed >= dot) {
            dotColor = wifiSpeed === 1 ? "bg-red-500" : "bg-green-500";
          } else if (wifiSpeed === 1 && dot === 1) {
            dotColor = "bg-red-500";
          }

          return (
            <div key={dot} className={`size-[3px] rounded-full ${dotColor}`} />
          );
        })}
      </div>
    );
  };

  return (
    <motion.fieldset
      className={cn(
        "absolute overflow-clip bottom-2 right-2 bg-black/20 bg-blend-multiply text-white p-1 px-2 z-20 flex flex-row gap-2 rounded-sm items-center justify-center",
        wifiSpeed === 1 &&
          "after:bg-red-700/50 after:animate-pulse after:absolute after:inset-0 text-white",
      )}
      variants={variants.player.footer}
      initial="initial"
      animate="animate"
    >
      <label className="font-medium z-20 text-xs flex flex-col gap-1 items-center justify-center">
        {wifiSpeed === 1 ? (
          <WifiOneBar className="size-4" />
        ) : wifiSpeed === 2 ? (
          <WifiTwoBar className="size-4" />
        ) : (
          <Wifi className="size-4" />
        )}
        {renderDots()}
      </label>
      {showText && (
        <div className="flex flex-col relative z-10">
          <span
            className="text-[10px] uppercase font-mono"
            style={{ textShadow: "0 0px 2px rgba(0, 0, 0, 0.5)" }}
          >
            Wifi signal
          </span>

          <span className="text-xs font-medium">
            {wifiSpeed === 1 ? "Poor" : wifiSpeed === 2 ? "Good" : "Excellent"}
          </span>
        </div>
      )}
    </motion.fieldset>
  );
};

export default function BeforeStarting() {
  const { context } = useInterviewStore();
  const t = useI18n();
  const items = [
    context?.duration
      ? {
          icon: <ClockFilled className="mt-1.5 size-5 min-w-5 text-gray-600" />,
          title: `Expect to spend ~${(context?.duration ?? 1) / 60}`,
          desc: "Very short interviews may not be considered complete. Please answer thoughtfully.",
        }
      : null,
    {
      icon: <VideocamFilled className="mt-1.5 size-5 min-w-5 text-gray-600" />,
      title: t("onboarding.descriptionList.testCamera.title"),
      desc: t("onboarding.descriptionList.testCamera.description"),
    },
    {
      icon: <MicFilled className="mt-1.5 size-5 min-w-5 text-gray-600" />,
      title: t("onboarding.descriptionList.testMicrophone.title"),
      desc: t("onboarding.descriptionList.testMicrophone.description"),
    },
    {
      icon: <VolumeUp className="mt-1.5 size-5 min-w-5 text-gray-600" />,
      title: t("onboarding.descriptionList.useReliableDevices.title"),
      desc: t("onboarding.descriptionList.useReliableDevices.description"),
    },
    {
      icon: <LockFilled className="mt-1.5 size-5 min-w-5 text-gray-600" />,
      title: t("onboarding.descriptionList.dataIsInYourControl.title"),
      desc: t("onboarding.descriptionList.dataIsInYourControl.description"),
    },
  ];

  return (
    <motion.div
      variants={variants.list.container}
      initial="initial"
      animate="animate"
      className="max-w-md mx-auto space-y-6"
    >
      {items.filter(Boolean).map((item, i) => (
        <motion.div
          key={i}
          className="flex items-start space-x-3"
          variants={variants.list.item}
        >
          {item!.icon}
          <div>
            <h3 className="text-md font-semibold text-gray-900">
              {item!.title}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {item!.desc}
            </p>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}
